import {Component, HostListener, OnDestroy, OnInit} from '@angular/core';
import {Location} from '@angular/common';
import {ProductionCatalogueService} from '../_service/production-catalogue.service';
import {combineLatest, map, Observable, of, startWith, Subject, Subscription, switchMap} from 'rxjs';
import {Page} from '../../shared/pagination/page';
import {Header} from '../../shared/table/header';
import {ProductionCatalogueOverview} from '../_model/production-catalogue-overview';
import {TranslateService} from '@ngx-translate/core';
import {ProductionCatalogue} from '../_model/production-catalogue';
import {ContentArray} from '../../shared/model/content-array';
import {DropdownData} from '../../shared/dropdown-multiselect/dropdown-data';
import {DropdownDataFactory} from '../../shared/dropdown-multiselect/dropdown-data.factory';
import {ActivatedRoute, Router} from '@angular/router';
import {RequestStatus} from '../_model/request-status';
import {DeleteObserverFactory} from '../../shared/delete/delete-observer-factory';

@Component({
	selector: 'app-production-catalogue-overview',
	templateUrl: './production-catalogue-overview.component.html',
	styleUrls: ['./production-catalogue-overview.component.css'],
	standalone: false
})
export class ProductionCatalogueOverviewComponent implements OnInit, OnDestroy {
	productionCatalogue$: Observable<ProductionCatalogue>;
	page$: Observable<Page<ProductionCatalogueOverview>>;
	headers: Header[] = [
		{name: 'status'},
		{name: 'approvalDate'},
		{name: 'identification'},
		{name: 'airac'},
		{name: 'requester'},
		{name: 'project'}
	];
	private refresh$ = new Subject<void>();
	private currentPage$ = new Subject<number>();
	selectedFilterCriteria = {} as ContentArray;
	searchValue = '';
	filterDropdownData$: Observable<DropdownData[]>;
	openedDropdown: number | null = null;
	private subscription = new Subscription();
	productionCatalogueUuid: string | null = null;


	constructor(private readonly productionCatalogueService: ProductionCatalogueService,
				private readonly deleteObserverFactory: DeleteObserverFactory,
				private readonly dropdownDataFactory: DropdownDataFactory,
				private readonly translateService: TranslateService,
				private readonly router: Router,
				private readonly route: ActivatedRoute,
				private readonly location: Location) {
	}

	ngOnInit(): void {
		this.route.params.subscribe((params): void => {
			this.productionCatalogueUuid = params['uuid'];
			if (!this.productionCatalogueUuid) return;

			this.productionCatalogueService.get(this.productionCatalogueUuid).subscribe((prodCat): void => {
				this.productionCatalogue$ = of(prodCat);
			});
		});

		this.page$ = combineLatest([this.currentPage$.pipe(startWith(1)), this.refresh$.pipe(startWith(''))])
			.pipe(
				switchMap(([currentPage]) => this.productionCatalogueService.search(currentPage - 1, this.selectedFilterCriteria, this.searchValue))
			);

		this.filterDropdownData$ = this.refresh$.pipe(
			startWith(''),
			switchMap(() => this.productionCatalogueService.getFilterCriteria()),
			map(filterCriteria => filterCriteria.map(criteria => this.dropdownDataFactory.createDropdownData(criteria)))
		);
	}

	ngOnDestroy(): void {
		this.subscription.unsubscribe();
	}

	loadPage(page: number): void {
		this.currentPage$.next(page);
	}

	closePage(): void {
		this.location.back();
	}

	deleteCatalogue(uuid: string): void {
		this.subscription.add(this.productionCatalogueService.delete(uuid)
			.subscribe(this.deleteObserverFactory.createFactory('production-catalogue.delete.success-message', this.refresh$))
		);
	}

	getConfirmationMessage(): string {
		return `<p>${this.translateService.instant('production-catalogue.delete.confirmation-message.are-you-sure')}</p>`;
	}

	openViewPage(uuid: string): void {
		this.router.navigate([`/production-catalogues/view/${uuid}`]);
	}

	openEditPage(uuid: string): void {
		this.router.navigate([`/production-catalogues/edit/${uuid}`]);
	}

	openCreatePage(): void {
		this.router.navigate(['/production-catalogues/create']);
	}

	openCopyPage(uuid: string): void {
		this.router.navigate([`/production-catalogues/copy/${uuid}`]);
	}

	openLaunchPage(uuid: string): void {
		this.router.navigate([`/production-catalogues/launch/${uuid}`]);
	}

	exportProductionCatalogue(productionCatalogue: ProductionCatalogueOverview): void {
		this.productionCatalogueService.downloadExport(productionCatalogue.uuid, productionCatalogue.identification);
	}

	exportProductionCatalogueArticles(productionCatalogue: ProductionCatalogueOverview): void {
		this.productionCatalogueService.downloadExportArticles(productionCatalogue.uuid, productionCatalogue.identification);
	}

	search(searchValue: any): void {
		this.searchValue = searchValue;
		this.loadPage(1);
	}

	filtersSelected(event: ContentArray): void {
		this.selectedFilterCriteria = {};
		Object.keys(event).forEach(key => this.selectedFilterCriteria[key] = event[key]);
		this.loadPage(1);
	}

	hasSearchCriteria(): boolean {
		return !!this.searchValue || Object.keys(this.selectedFilterCriteria).length > 0;
	}

	@HostListener('document:click', ['$event'])
	onDocumentClick(event: MouseEvent): void {
		const target = event.target as HTMLElement;

		if (!target.closest('.dropbtn-dropdown')) {
			this.openedDropdown = null;
		}
	}

	protected readonly RequestStatus = RequestStatus;
}
